import { useEffect, useState } from "react";
import api from "@/lib/api";
import { INDIAN_STATES } from "@/lib/indian_states";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MapPin } from "lucide-react";

/**
 * Two-step header filter: pick a State, then optionally narrow to a
 * District. Mirrors the look of EmployeeFilter / ManufacturerFilter so all
 * three live happily side-by-side in the same toolbar.
 *
 * Props:
 *   state     — current state filter ("" = all states)
 *   district  — current district filter ("" = all districts in the picked state)
 *   onChange  — ({state, district}) => void  fired whenever either value changes
 *   testId    — optional data-testid prefix (default "state-district-filter")
 *
 * District dropdown is hidden until a state is picked — keeps the toolbar
 * compact for users who only filter by state.
 */
export default function StateDistrictFilter({ state = "", district = "", onChange, testId = "state-district-filter" }) {
  const [districts, setDistricts] = useState([]);

  useEffect(() => {
    if (!state) { setDistricts([]); return; }
    let cancelled = false;
    api
      .get("/marketing-contacts/districts", { params: { state } })
      .then((r) => { if (!cancelled) setDistricts(r.data?.districts || []); })
      .catch(() => { if (!cancelled) setDistricts([]); });
    return () => { cancelled = true; };
  }, [state]);

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <Select
        value={state || "__all"}
        onValueChange={(v) => onChange({ state: v === "__all" ? "" : v, district: "" })}
      >
        <SelectTrigger className="rounded-2xl h-11 w-full sm:w-48" data-testid={`${testId}-state`}>
          <MapPin className="h-4 w-4 mr-1.5 text-primary shrink-0" />
          <SelectValue placeholder="All states" />
        </SelectTrigger>
        <SelectContent className="rounded-2xl max-h-80">
          <SelectItem value="__all">All states</SelectItem>
          {INDIAN_STATES.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}
        </SelectContent>
      </Select>
      {state && (
        <Select
          value={district || "__all"}
          onValueChange={(v) => onChange({ state, district: v === "__all" ? "" : v })}
        >
          <SelectTrigger className="rounded-2xl h-11 w-full sm:w-48" data-testid={`${testId}-district`}>
            <SelectValue placeholder="All districts" />
          </SelectTrigger>
          <SelectContent className="rounded-2xl max-h-80">
            <SelectItem value="__all">All districts</SelectItem>
            {districts.map((d) => (<SelectItem key={d} value={d}>{d}</SelectItem>))}
          </SelectContent>
        </Select>
      )}
    </div>
  );
}
